import React, { useState, useEffect } from "react";
import { SelectionTool, SelectionMode } from "./types";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "../../../ui/DropdownMenu";
import { useTheme } from "../../../../contexts/ThemeContext";
import ToolSelectionIcon from "../../../../media/ToolSelectionIcon";
import { selectionTools } from "../../../constants/mapConstants";
import NoToolIcon from "../../../../media/NoToolIcon";
import MapTooltip from "../../MapTooltip";

interface MapToolsMenuProps {
    activeTool: SelectionTool;
    selectionMode: SelectionMode;
    onToolChange: (tool: SelectionTool) => void;
    onModeChange: (mode: SelectionMode) => void;
    className?: string;
}

const MapToolsMenu: React.FC<MapToolsMenuProps> = ({
    activeTool,
    selectionMode,
    onToolChange,
    onModeChange,
}) => {
    const [toolDropdownOpen, setToolDropdownOpen] = useState(false);
    const [previousTool, setPreviousTool] = useState<SelectionTool>(null);
    const { theme } = useTheme();

    // Update previousTool whenever activeTool changes
    useEffect(() => {
        if (activeTool) {
            setPreviousTool(activeTool);
        }
    }, [activeTool]);

    // Add ESC event listener
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onToolChange(null);
            }
        };
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onToolChange]);

    return (
        <div className="flex flex-col">
            <MapTooltip 
                button={(
                    <button
                        className={`${
                            activeTool === null
                                ? "bg-[#C6D92D]"
                                : "bg-white dark:bg-[#2D3748] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200"
                        } w-[24px] h-[24px] flex items-center justify-center cursor-pointer focus:ring-none rounded-t-[4px]`}
                        onClick={() => {
                            onToolChange(null);
                            setToolDropdownOpen(false);
                        }}
                    >
                        <NoToolIcon 
                            fillClassName={
                                activeTool === null ? "#2D3748" :
                                theme === "light" ? "#535353" : "#7A8690" 
                            } 
                        />
                    </button>
                )}
                text="Hand tool"
            />
            <MapTooltip
                button={(
                    <button
                        className={`${
                            activeTool
                                ? "bg-[#C6D92D]"
                                : "bg-white dark:bg-[#2D3748] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200"
                        } w-[24px] h-[24px] flex items-center justify-center cursor-pointer focus:ring-none duration-200`}
                        onClick={() => {
                            if (activeTool) {
                                setPreviousTool(activeTool);
                                onToolChange(null);
                            } else {
                                onToolChange(
                                    previousTool ||
                                        (selectionTools[0].id as SelectionTool)
                                );
                            }
                            setToolDropdownOpen(false);
                        }}
                    >
                        {(() => {
                            // If there's an active tool, show it
                            if (activeTool) {
                                const currentTool = selectionTools.find(
                                    (tool) => tool.id === activeTool
                                );
                                return React.createElement(
                                    currentTool?.icon || selectionTools[0].icon,
                                    {
                                        fillClassName: "#2D3748",
                                    }
                                );
                            }

                            // If there's no active tool but there is a previous tool, show the previous tool
                            if (previousTool) {
                                const prevTool = selectionTools.find(
                                    (tool) => tool.id === previousTool
                                );
                                return React.createElement(
                                    prevTool?.icon || selectionTools[0].icon,
                                    {
                                        fillClassName:
                                            theme === "light" ? "#535353" : "#7A8690",
                                    }
                                );
                            }

                            // Default case: show first tool
                            return React.createElement(selectionTools[0].icon, {
                                fillClassName:
                                    theme === "light" ? "#535353" : "#7A8690",
                            });
                        })()}
                    </button>
                )}
                text={`${(activeTool || previousTool) || selectionTools[0].id} tool`}
            />
            <DropdownMenu
                open={toolDropdownOpen}
                onOpenChange={setToolDropdownOpen}
            >
                <DropdownMenuTrigger
                    className={`${
                        toolDropdownOpen
                            ? "bg-[#C6D92D]"
                            : "bg-white dark:bg-[#2D3748] hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200" 
                    } w-[24px] h-[16px] flex items-center justify-center cursor-pointer focus:ring-none duration-300 rounded-b-[4px]`}
                >
                    <ToolSelectionIcon
                        theme={theme}
                        isOpen={toolDropdownOpen}
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    side="left"
                    sideOffset={8}
                    alignOffset={-24}
                    className="flex flex-col rounded-[4px] px-[12px] py-[8px] bg-white dark:bg-[#2D3748]"
                >
                    <span className="text-[#535353] dark:text-[#CEE0EF] text-[14px] font-bold">
                        Selection Tool
                    </span>
                    <div className="flex justify-between pt-[8px]">
                        {selectionTools.map(({ id, icon: Icon }) => (
                            <div key={id} className="flex flex-col items-center gap-1">
                                <button
                                    onClick={() => {
                                        onToolChange(id as SelectionTool);
                                        setToolDropdownOpen(false);
                                    }}
                                    className={`flex flex-col items-center justify-center rounded-full h-[24px] w-[24px] ${
                                        activeTool === id
                                            ? "bg-[#C6D92D] text-black dark:text-white"
                                            : "bg-[#CEE0EF] dark:bg-[#7A8590] text-[#535353] dark:text-[#CEE0EF]"
                                    } duration-300`}
                                >
                                    <Icon
                                        fillClassName={
                                            activeTool === id
                                                ? "#2D3748"
                                                : theme === "light"
                                                  ? "#535353"
                                                  : "#2D3748"
                                        }
                                    />
                                </button>
                                <span className="text-[10px]">{id}</span>
                            </div>
                        ))}
                    </div>
                    <span className="text-[#535353] dark:text-[#CEE0EF] text-[14px] font-bold py-2">
                        Selection Mode
                    </span>
                    <div className="flex flex-col gap-1">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <div className="w-[16px] h-[16px] rounded-full border-[1px] flex items-center justify-center border-[#C6C5C5] dark:border-[#7A8690]">
                                {selectionMode === "within" && (
                                    <div className="w-[8px] h-[8px] rounded-full bg-[#C6D92D]" />
                                )}
                            </div>
                            <input
                                type="radio"
                                name="selectionMode"
                                value="within"
                                checked={selectionMode === "within"}
                                onChange={() => onModeChange("within")}
                                className="sr-only"
                            />
                            <span className="text-[11px]">
                                Partially or Completely Within
                            </span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer pb-1">
                            <div className="w-[16px] h-[16px] rounded-full border-[1px] flex items-center justify-center border-[#C6C5C5] dark:border-[#7A8690]">
                                {selectionMode === "contained" && (
                                    <div className="w-[8px] h-[8px] rounded-full bg-[#C6D92D]" />
                                )}
                            </div>
                            <input
                                type="radio"
                                name="selectionMode"
                                value="contained"
                                checked={selectionMode === "contained"}
                                onChange={() => onModeChange("contained")}
                                className="sr-only"
                            />
                            <span className="text-[11px]">
                                Completely Contained By
                            </span>
                        </label>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default MapToolsMenu;